import React from 'react';
import { useIntl, useLocation } from 'umi';
import { Alert } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import useCOS, { getUseAccelerateByENV } from '@gp/hooks/useCos/useCOS';
import useLowcodeCosTemplate from './_hooks/useLowcodeCosTemplate';
import useIframeDataInjector from './_hooks/useIframeDataInjector';

import type { IFetchTencentQCouldMessage } from '@intelligence/services/cos';

import '@intelligence/pages/LowCodeTemplate/index.scss';

export interface ILowCodeTemplateDataJsonProps extends IFetchTencentQCouldMessage {
  config: IRecord;
}

const LowCodeTemplateDataJson: React.FC<ILowCodeTemplateDataJsonProps> = (props) => {
  const { config, fetchTencentQCouldMessage } = props;

  const location = useLocation();
  const { locale } = useIntl();

  // Get parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const title = searchParams.get('title');

  const useAccelerate = getUseAccelerateByENV();

  const { isCOSInited, getObject, getObjectUrl } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchTencentQCouldMessage,
    authorizationFetcher: fetchTencentQCouldMessage,
  });

  const { loading, error, htmlContent, jsonData } = useLowcodeCosTemplate({
    templateConfig: config,
    locale,
    isCOSInited,
    getObject,
    getObjectUrl,
  });

  const { iframeRef, handleIframeLoad } = useIframeDataInjector({
    htmlContent,
    jsonData,
  });

  if (loading) {
    return (
      <div
        style={{
          height: 'calc(100vh - 180px)',
          color: '#262832',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbi-cos-page-error">
        <Alert>Failed to load content: {error instanceof Error ? error.message : 'Unknown error'}</Alert>
      </div>
    );
  }

  return (
    <div className="dbi-cos-page-wrapper">
      {(config?.title || title) && <h1 className="dbi-cos-page-title">{config?.title || title}</h1>}
      <div className="dbi-cos-page-container">
        <iframe
          ref={iframeRef}
          className="dbi-cos-content-iframe"
          srcDoc={htmlContent}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          title="Low-code template content"
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
};

export default LowCodeTemplateDataJson;
